import React, { useState, useEffect, useCallback } from 'react';
import { adminApi } from '../../services/api';
import { 
  FiBarChart2, 
  FiCalendar, 
  FiUsers, 
  FiClock,
  FiAlertCircle,
  FiTrendingUp,
  FiTrendingDown,
  FiFilter,
  FiDownload,
  FiCheckCircle,
  FiXCircle
} from 'react-icons/fi';
import toast from 'react-hot-toast';
import { format, parseISO, startOfWeek, endOfWeek, startOfMonth, endOfMonth } from 'date-fns';
import { 
  BarChart, 
  Bar, 
  LineChart, 
  Line, 
  PieChart, 
  Pie, 
  Cell,
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer,
  ReferenceLine 
} from 'recharts';

function AdminStatisticsPage() {
  const [stats, setStats] = useState({
    users: { total: 0, active: 0, pending: 0, admins: 0 },
    reservations: { total: 0, active: 0, cancelled: 0 },
    utilization: 0,
    mostActiveUsers: [],
    reservationsByDay: []
  });
  const [timeRange, setTimeRange] = useState('month'); // 'week', 'month', 'quarter', 'year'
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [chartType, setChartType] = useState('bar'); // 'bar', 'line', 'pie'

  // Colors for charts
  const COLORS = ['#2563eb', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#84cc16'];

  // Fetch statistics
  const fetchStatistics = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      // Calculate date range
      const now = new Date();
      let startDate, endDate;
      
      switch (timeRange) {
        case 'week':
          startDate = format(startOfWeek(now), 'yyyy-MM-dd');
          endDate = format(endOfWeek(now), 'yyyy-MM-dd');
          break;
        case 'month':
          startDate = format(startOfMonth(now), 'yyyy-MM-dd');
          endDate = format(endOfMonth(now), 'yyyy-MM-dd');
          break;
        case 'quarter':
          startDate = format(new Date(now.getFullYear(), now.getMonth() - 3, 1), 'yyyy-MM-dd');
          endDate = format(now, 'yyyy-MM-dd');
          break;
        case 'year':
          startDate = format(new Date(now.getFullYear(), 0, 1), 'yyyy-MM-dd');
          endDate = format(now, 'yyyy-MM-dd');
          break;
        default:
          startDate = format(new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000), 'yyyy-MM-dd');
          endDate = format(now, 'yyyy-MM-dd');
      }
      
      const response = await adminApi.getStatistics({
        startDate,
        endDate
      });
      
      setStats(response.data.statistics);
    } catch (err) {
      console.error('Error fetching statistics:', err);
      setError('Nepodařilo se načíst statistiky. Zkuste to prosím znovu.');
      toast.error('Chyba při načítání statistik');
    } finally {
      setIsLoading(false);
    }
  }, [timeRange]);

  useEffect(() => {
    fetchStatistics();
  }, [fetchStatistics]);

  // Format number with locale
  const formatNumber = (num) => {
    return new Intl.NumberFormat('cs-CZ').format(num);
  };

  // Format percentage
  const formatPercentage = (num) => {
    return (num || 0).toFixed(1) + '%';
  };

  // Format date for display
  const formatDateRange = () => {
    const now = new Date();
    
    switch (timeRange) {
      case 'week':
        return `Týden: ${format(startOfWeek(now), 'dd.MM.')} - ${format(endOfWeek(now), 'dd.MM.yyyy')}`;
      case 'month':
        return `Měsíc: ${format(startOfMonth(now), 'MM.yyyy')}`;
      case 'quarter':
        return `Poslední 3 měsíce`;
      case 'year':
        return `Rok: ${now.getFullYear()}`;
      default:
        return `Poslední 30 dní`;
    }
  };

  // Prepare chart data
  const prepareChartData = () => {
    switch (chartType) {
      case 'bar':
        // Reservations by day
        return stats.reservationsByDay.map(day => ({
          date: day.date,
          count: day.count
        }));
      
      case 'line':
        // Cumulative reservations
        let cumulative = 0;
        return stats.reservationsByDay.map(day => {
          cumulative += day.count;
          return {
            date: day.date,
            count: cumulative
          };
        });
      
      case 'pie':
        // Most active users
        return stats.mostActiveUsers.map(user => ({
          name: `${user.firstName} ${user.lastName}`,
          value: user.reservationCount
        }));
      
      default:
        return [];
    }
  };

  // Calculate growth rates
  const calculateGrowthRates = () => {
    // This would require historical data, for now we'll use mock data
    return {
      users: '+12%',
      reservations: '+8%',
      utilization: stats.utilization > 0 ? `+${stats.utilization.toFixed(1)}%` : '0%'
    };
  };

  const growthRates = calculateGrowthRates();

  // Get time range label
  const getTimeRangeLabel = () => {
    switch (timeRange) {
      case 'week': return 'Týden';
      case 'month': return 'Měsíc';
      case 'quarter': return 'Čtvrtletí';
      case 'year': return 'Rok';
      default: return 'Poslední 30 dní';
    }
  };

  // Export data as CSV
  const exportData = () => {
    try {
      // Prepare CSV data
      let csv = 'Datum,Počet rezervací\n';
      
      switch (chartType) {
        case 'bar':
        case 'line':
          stats.reservationsByDay.forEach(day => {
            csv += `${day.date},${day.count}\n`;
          });
          break;
        case 'pie':
          csv = 'Uživatel,Počet rezervací\n';
          stats.mostActiveUsers.forEach(user => {
            csv += `"${user.firstName} ${user.lastName}",${user.reservationCount}\n`;
          });
          break;
      }
      
      // Create download link
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `statistiky-${timeRange}-${format(new Date(), 'yyyy-MM-dd')}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast.success('Data exportována jako CSV');
    } catch (err) {
      console.error('Error exporting data:', err);
      toast.error('Nepodařilo se exportovat data');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Statistiky systému</h1>
        <p className="text-sm text-gray-600 mt-1">
          Analýza využití a výkonnosti rezervačního systému
        </p>
      </div>

      {/* Loading state */}
      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <div className="loading-spinner"></div>
        </div>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg flex items-center">
          <FiAlertCircle className="mr-2" />
          {error}
        </div>
      ) : (
        <>
          {/* Time Range Selector */}
          <div className="bg-white rounded-xl shadow-md p-4 flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center space-x-2">
              <FiCalendar className="text-blue-600" />
              <span className="text-gray-600">{formatDateRange()}</span>
            </div>
            
            <div className="flex bg-white border border-gray-200 rounded-lg shadow-sm">
              <button
                onClick={() => setTimeRange('week')}
                className={`px-4 py-2 text-sm font-medium rounded-l-lg transition-colors ${
                  timeRange === 'week' 
                    ? 'bg-blue-100 text-blue-700' 
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                Týden
              </button>
              <button
                onClick={() => setTimeRange('month')}
                className={`px-4 py-2 text-sm font-medium border-l border-r border-gray-200 transition-colors ${
                  timeRange === 'month' 
                    ? 'bg-blue-100 text-blue-700' 
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                Měsíc
              </button>
              <button
                onClick={() => setTimeRange('quarter')}
                className={`px-4 py-2 text-sm font-medium border-r border-gray-200 transition-colors ${
                  timeRange === 'quarter' 
                    ? 'bg-blue-100 text-blue-700' 
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                Čtvrtletí
              </button>
              <button
                onClick={() => setTimeRange('year')}
                className={`px-4 py-2 text-sm font-medium rounded-r-lg transition-colors ${
                  timeRange === 'year' 
                    ? 'bg-blue-100 text-blue-700' 
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                Rok
              </button>
            </div>
          </div>

          {/* Key Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Total Users */}
            <div className="bg-white rounded-xl shadow-md p-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-medium text-gray-500">Celkem uživatelů</h3>
                  <p className="text-3xl font-bold text-gray-900 mt-2">
                    {formatNumber(stats.users.total)}
                  </p>
                  <p className="text-sm text-green-600 font-medium mt-1">
                    <FiTrendingUp className="inline mr-1" />
                    {growthRates.users}
                  </p>
                </div>
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                  <FiUsers className="text-xl text-blue-600" />
                </div>
              </div>
            </div>

            {/* Active Users */}
            <div className="bg-white rounded-xl shadow-md p-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-medium text-gray-500">Aktivní uživatelé</h3>
                  <p className="text-3xl font-bold text-gray-900 mt-2">
                    {formatNumber(stats.users.active)}
                  </p>
                  <p className="text-sm text-blue-600 font-medium mt-1">
                    {Math.round((stats.users.active / stats.users.total) * 100)}% z celku
                  </p>
                </div>
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                  <FiCheckCircle className="text-xl text-green-600" />
                </div>
              </div>
            </div>

            {/* Total Reservations */}
            <div className="bg-white rounded-xl shadow-md p-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-medium text-gray-500">Celkem rezervací</h3>
                  <p className="text-3xl font-bold text-gray-900 mt-2">
                    {formatNumber(stats.reservations.total)}
                  </p>
                  <p className="text-sm text-green-600 font-medium mt-1">
                    <FiTrendingUp className="inline mr-1" />
                    {growthRates.reservations}
                  </p>
                </div>
                <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                  <FiCalendar className="text-xl text-purple-600" />
                </div>
              </div>
            </div>

            {/* Active Reservations */}
            <div className="bg-white rounded-xl shadow-md p-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-medium text-gray-500">Aktivní rezervace</h3>
                  <p className="text-3xl font-bold text-gray-900 mt-2">
                    {formatNumber(stats.reservations.active)}
                  </p>
                  <p className="text-sm text-blue-600 font-medium mt-1">
                    {Math.round((stats.reservations.active / stats.reservations.total) * 100)}% aktivních
                  </p>
                </div>
                <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                  <FiClock className="text-xl text-orange-600" />
                </div>
              </div>
            </div>
          </div>

          {/* Additional Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Room Utilization */}
            <div className="bg-white rounded-xl shadow-md p-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-medium text-gray-500">Využití místnosti</h3>
                  <p className="text-3xl font-bold text-blue-600 mt-2">
                    {formatPercentage(stats.utilization)}
                  </p>
                  <p className="text-sm text-gray-600 mt-1">
                    Z celkového času
                  </p>
                </div>
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                  <FiBarChart2 className="text-xl text-blue-600" />
                </div>
              </div>
              <div className="mt-4 w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-blue-600 h-2 rounded-full" 
                  style={{ width: `${stats.utilization}%` }}
                ></div>
              </div>
            </div>

            {/* Cancelled Reservations */}
            <div className="bg-white rounded-xl shadow-md p-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-medium text-gray-500">Zrušené rezervace</h3>
                  <p className="text-3xl font-bold text-red-600 mt-2">
                    {formatNumber(stats.reservations.cancelled)}
                  </p>
                  <p className="text-sm text-gray-600 mt-1">
                    {Math.round((stats.reservations.cancelled / stats.reservations.total) * 100)}% z celku
                  </p>
                </div>
                <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
                  <FiXCircle className="text-xl text-red-600" />
                </div>
              </div>
            </div>

            {/* Pending Users */}
            <div className="bg-white rounded-xl shadow-md p-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-medium text-gray-500">Čekající uživatelé</h3>
                  <p className="text-3xl font-bold text-yellow-600 mt-2">
                    {formatNumber(stats.users.pending)}
                  </p>
                  <p className="text-sm text-gray-600 mt-1">
                    {Math.round((stats.users.pending / stats.users.total) * 100)}% z celku
                  </p>
                </div>
                <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
                  <FiAlertCircle className="text-xl text-yellow-600" />
                </div>
              </div>
            </div>
          </div>

          {/* Chart Type Selector */}
          <div className="bg-white rounded-xl shadow-md p-4 flex flex-wrap items-center justify-between gap-4">
            <h3 className="font-semibold text-gray-900">Vývoj rezervací</h3>
            
            <div className="flex bg-white border border-gray-200 rounded-lg shadow-sm">
              <button
                onClick={() => setChartType('bar')}
                className={`px-4 py-2 text-sm font-medium rounded-l-lg transition-colors ${
                  chartType === 'bar' 
                    ? 'bg-blue-100 text-blue-700' 
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                Sloupce
              </button>
              <button
                onClick={() => setChartType('line')}
                className={`px-4 py-2 text-sm font-medium border-l border-r border-gray-200 transition-colors ${
                  chartType === 'line' 
                    ? 'bg-blue-100 text-blue-700' 
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                Čára
              </button>
              <button
                onClick={() => setChartType('pie')}
                className={`px-4 py-2 text-sm font-medium rounded-r-lg transition-colors ${
                  chartType === 'pie' 
                    ? 'bg-blue-100 text-blue-700' 
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                Koláč
              </button>
            </div>
          </div>

          {/* Chart */}
          <div className="bg-white rounded-xl shadow-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-gray-900">
                {chartType === 'bar' && 'Počet rezervací podle dne'}
                {chartType === 'line' && 'Kumulativní počet rezervací'}
                {chartType === 'pie' && 'Nejaktivnější uživatelé'}
              </h3>
              
              <button
                onClick={exportData}
                className="flex items-center px-3 py-2 text-sm border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <FiDownload className="mr-2" />
                Exportovat
              </button>
            </div>
            
            <div className="h-64">
              {chartType === 'bar' && (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={prepareChartData()}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="date" 
                      tickFormatter={(value) => format(parseISO(value), 'dd.MM.')}
                    />
                    <YAxis />
                    <Tooltip 
                      labelFormatter={(value) => format(parseISO(value), 'dd.MM.yyyy')}
                    />
                    <Bar dataKey="count" fill="#2563eb" name="Počet rezervací" />
                  </BarChart>
                </ResponsiveContainer>
              )}
              
              {chartType === 'line' && (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={prepareChartData()}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="date" 
                      tickFormatter={(value) => format(parseISO(value), 'dd.MM.')}
                    />
                    <YAxis />
                    <Tooltip 
                      labelFormatter={(value) => format(parseISO(value), 'dd.MM.yyyy')}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="count" 
                      stroke="#2563eb" 
                      strokeWidth={2}
                      name="Kumulativní počet"
                    />
                  </LineChart>
                </ResponsiveContainer>
              )}
              
              {chartType === 'pie' && (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={prepareChartData()}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                      nameKey="name"
                      label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                    >
                      {prepareChartData().map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>

          {/* Most Active Users Table */}
          <div className="bg-white rounded-xl shadow-md p-6">
            <h3 className="font-semibold text-gray-900 mb-4">Nejaktivnější uživatelé</h3>
            
            {stats.mostActiveUsers.length === 0 ? (
              <div className="text-center py-8">
                <FiUsers className="w-12 h-12 text-gray-200 mx-auto mb-2" />
                <p className="text-gray-500">Žádní aktivní uživatelé</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full">
                  <thead className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <tr>
                      <th className="text-left px-4 py-3">Pořadí</th>
                      <th className="text-left px-4 py-3">Uživatel</th>
                      <th className="text-left px-4 py-3">Email</th>
                      <th className="text-right px-4 py-3">Počet rezervací</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {stats.mostActiveUsers.map((user, index) => (
                      <tr key={user.email} className="hover:bg-gray-50 transition-colors">
                        <td className="px-4 py-3 text-gray-900">{index + 1}.</td>
                        <td className="px-4 py-3 font-medium text-gray-900">
                          {user.firstName} {user.lastName}
                        </td>
                        <td className="px-4 py-3 text-gray-500">{user.email}</td>
                        <td className="px-4 py-3 text-right font-medium text-blue-600">
                          {user.reservationCount}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default AdminStatisticsPage;
